import { Column } from 'primereact/column';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ElectricityEnum, TypeOfElectricityMixEnum } from 'services/EpdClient';
import { EPD_DICTIONARY_CODES } from 'services/api/constants';
import { useDictionaryValues } from 'services/api/queries';
import { HeaderCaptionSemiBold } from 'styles/v2/Styles.styled';
import { ElectricityDataModel, EnergySourceModel } from 'types/types';
import { getOptionsFromDictionary } from 'util/utils';
import { DataTableStyled } from '../StyledGrid';

type TRow = {
  key: number;
  field: ElectricityEnum;
  fieldName: string;
  source: string | undefined;
  value: string | number | EnergySourceModel[] | null | undefined;
};

type TProps = {
  headerText: string;
  data: ElectricityDataModel;
  isPcrV2: boolean;
  className?: string;
};

const ElectricityGridPdf = ({ headerText, data, isPcrV2, className }: TProps) => {
  const electricityTable = useRef<any>(null);
  const { t } = useTranslation();

  const dictionariesQuery = useDictionaryValues();
  const typeOfElectricityMixRawOptions = getOptionsFromDictionary(
    dictionariesQuery.data,
    EPD_DICTIONARY_CODES.TYPE_OF_ELECTRICITY_MIX
  );

  const typeOfElectricityMixOptions = isPcrV2
    ? typeOfElectricityMixRawOptions.filter((x) => x.value !== TypeOfElectricityMixEnum.ResidualOfSupplierOnMarket)
    : typeOfElectricityMixRawOptions;

  // ToDo: refactor this.
  // Primereact Datatable doesn't support both rowspan and colspan column at the same time for content rows.
  // Rowspan is configured in Datatable, that why colspan is set on every rendering of the Electricity table
  useEffect(() => {
    if (electricityTable.current) {
      const table = electricityTable.current.getTable();
      const tbody = table.getElementsByTagName('tbody');
      const rows = tbody[0].getElementsByTagName('tr');
      if (rows.length >= 13) {
        setColSpan(rows, 0);
        setColSpan(rows, rows.length - 1);
      }
    }
  });

  const setColSpan = (rows: any, rowIndex: number) => {
    const cells = rows[rowIndex].getElementsByTagName('td');
    cells[1].style = 'display: none';
    cells[cells.length - 1].colSpan = 2;
  };

  const getColumnsData = (data: ElectricityDataModel | undefined) => {
    let index = 0;
    const columns = [
      {
        key: index,
        field: ElectricityEnum.TypeOfElectricityMix,
        fieldName: 'Type of electricity mix',
        value: data?.typeOfElectricityMix,
      },
    ] as TRow[];

    data?.energySources?.forEach((item) => {
      columns.push({
        key: ++index,
        field: ElectricityEnum.EnergySource,
        fieldName: 'Energy sources',
        source: t(`epdEnergySource.${item.source}`),
        value: item.value?.toString(),
      });
    });

    columns.push({
      key: ++index,
      field: ElectricityEnum.ClimateImpact,
      fieldName: 'Climate impact (GWP-GHG):',
      value: data?.climateImpact,
    } as TRow);

    return columns;
  };

  const header = (
    <div className="flex flex-wrap align-items-center justify-content-between gap-2">
      <HeaderCaptionSemiBold>{headerText}</HeaderCaptionSemiBold>
    </div>
  );

  const valueBodyTemplate = (rowData: TRow) => {
    switch (rowData.field) {
      case ElectricityEnum.TypeOfElectricityMix:
        const typeOfElectricityMixLabel = typeOfElectricityMixOptions.find(
          (x) => Number(x.value) === data?.typeOfElectricityMix
        )?.label;
        return typeOfElectricityMixLabel;
      case ElectricityEnum.EnergySource:
        const sourceValue = rowData.value as number | undefined;
        return sourceValue ? `${sourceValue}%` : '';
      case ElectricityEnum.ClimateImpact:
        const numberValue = rowData.value as number | undefined;
        return numberValue ? (
          <>
            {numberValue} kg CO<sub>2</sub> eq./kWh
          </>
        ) : (
          ''
        );
      default:
        const stringValue = rowData.value as string | undefined;
        return stringValue ?? '';
    }
  };

  return (
    <DataTableStyled
      style={{ overflow: 'auto', display: 'grid' }}
      className={className}
      header={header}
      value={getColumnsData(data)}
      dataKey="key"
      showGridlines
      rowGroupMode="rowspan"
      groupRowsBy="fieldName"
      ref={electricityTable}
    >
      <Column field="fieldName" headerStyle={{ display: 'none' }} style={{ verticalAlign: 'top' }} />
      <Column field="source" headerStyle={{ display: 'none' }} style={{ width: '36%' }} />
      <Column field="value" body={valueBodyTemplate} headerStyle={{ display: 'none' }} style={{ width: '36%' }} />
    </DataTableStyled>
  );
};

export default ElectricityGridPdf;
